import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, GripVertical, FileText, Building2, AlertCircle, Lock, Unlock, Search, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { commandFilter } from '@/lib/commandFilter';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { LoanType } from '@/types';
import type { ChecklistTemplate, ChecklistForm, ChecklistCategory, ChecklistItem } from '@/types/checklist';
import {
  getAllChecklistTemplates,
  createChecklistTemplate,
  updateChecklistTemplate,
  getAllLenderChecklistForms,
  createLenderChecklistForm,
  updateLenderChecklistForm,
} from '@/services/checklistService';

const LOAN_TYPES: { value: LoanType; label: string }[] = [
  { value: 'home_loan', label: 'Home Loan' },
  { value: 'lap', label: 'Loan Against Property' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'business_loan', label: 'Business Loan' },
  { value: 'car_loan', label: 'Car Loan' },
];

// Comprehensive list of lenders (37 major banks and NBFCs)
const LENDERS = [
  // Public Sector Banks
  { id: 'state_bank_of_india', name: 'State Bank of India' },
  { id: 'bank_of_baroda', name: 'Bank of Baroda' },
  { id: 'punjab_national_bank', name: 'Punjab National Bank' },
  { id: 'canara_bank', name: 'Canara Bank' },
  { id: 'union_bank_of_india', name: 'Union Bank of India' },
  { id: 'bank_of_india', name: 'Bank of India' },
  { id: 'indian_bank', name: 'Indian Bank' },
  { id: 'central_bank_of_india', name: 'Central Bank of India' },
  { id: 'indian_overseas_bank', name: 'Indian Overseas Bank' },
  { id: 'uco_bank', name: 'UCO Bank' },
  
  // Private Sector Banks
  { id: 'hdfc_bank', name: 'HDFC Bank' },
  { id: 'icici_bank', name: 'ICICI Bank' },
  { id: 'axis_bank', name: 'Axis Bank' },
  { id: 'kotak_mahindra_bank', name: 'Kotak Mahindra Bank' },
  { id: 'indusind_bank', name: 'IndusInd Bank' },
  { id: 'yes_bank', name: 'Yes Bank' },
  { id: 'idfc_first_bank', name: 'IDFC First Bank' },
  { id: 'federal_bank', name: 'Federal Bank' },
  { id: 'rbl_bank', name: 'RBL Bank' },
  { id: 'bandhan_bank', name: 'Bandhan Bank' },
  
  // NBFCs
  { id: 'bajaj_finance', name: 'Bajaj Finance' },
  { id: 'tata_capital', name: 'Tata Capital' },
  { id: 'l_and_t_finance', name: 'L&T Finance' },
  { id: 'aditya_birla_finance', name: 'Aditya Birla Finance' },
  { id: 'mahindra_finance', name: 'Mahindra Finance' },
  { id: 'cholamandalam_finance', name: 'Cholamandalam Finance' },
  { id: 'shriram_finance', name: 'Shriram Finance' },
  { id: 'piramal_finance', name: 'Piramal Finance' },
  { id: 'hdb_financial_services', name: 'HDB Financial Services' },
  { id: 'iifl_finance', name: 'IIFL Finance' },
  
  // Housing Finance Companies
  { id: 'lic_housing_finance', name: 'LIC Housing Finance' },
  { id: 'hdfc_ltd', name: 'HDFC Ltd' },
  { id: 'pnb_housing_finance', name: 'PNB Housing Finance' },
  { id: 'india_bulls_housing_finance', name: 'India Bulls Housing Finance' },
  { id: 'godrej_housing_finance', name: 'Godrej Housing Finance' },
  { id: 'tata_capital_housing_finance', name: 'Tata Capital Housing Finance' },
  { id: 'aditya_birla_housing_finance', name: 'Aditya Birla Housing Finance' },
];

type EditMode = 'template' | 'lender';

// Hook for detecting mobile view
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

export function ChecklistManagement() {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'templates' | 'lenders'>('templates');
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [lenderForms, setLenderForms] = useState<ChecklistForm[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>('template');
  const [editingItem, setEditingItem] = useState<ChecklistTemplate | ChecklistForm | null>(null);
  const [editingCategories, setEditingCategories] = useState<ChecklistCategory[]>([]);
  const [editingName, setEditingName] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingLoanType, setEditingLoanType] = useState<LoanType>('home_loan');
  const [editingLenderId, setEditingLenderId] = useState('');
  const [editingLenderName, setEditingLenderName] = useState('');
  const [editingStatus, setEditingStatus] = useState<'draft' | 'published'>('draft');
  const [saving, setSaving] = useState(false);
  const [lenderComboboxOpen, setLenderComboboxOpen] = useState(false);

  // Category editor
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');

  // Item editor
  const [itemEditorOpen, setItemEditorOpen] = useState(false);
  const [editingItemCategoryIndex, setEditingItemCategoryIndex] = useState<number | null>(null);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemHelpText, setItemHelpText] = useState('');
  const [itemRequired, setItemRequired] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesData, formsData] = await Promise.all([
        getAllChecklistTemplates(),
        getAllLenderChecklistForms(),
      ]);
      setTemplates(templatesData);
      setLenderForms(formsData);
    } catch (error: any) {
      toast.error('Failed to load checklists');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open editor for new template
  const handleCreateTemplate = (loanType?: LoanType) => {
    setEditMode('template');
    setEditingItem(null);
    setEditingLoanType(loanType || 'home_loan');
    setEditingName('');
    setEditingDescription('');
    setEditingCategories([]);
    setEditorOpen(true);
  };

  // Open editor for existing template
  const handleEditTemplate = (template: ChecklistTemplate) => {
    setEditMode('template');
    setEditingItem(template);
    setEditingLoanType(template.loanType);
    setEditingName(template.name);
    setEditingDescription(template.description || '');
    setEditingCategories(JSON.parse(JSON.stringify(template.categories))); // Deep clone
    setEditorOpen(true);
  };

  // Open editor for new lender form
  const handleCreateLenderForm = (loanType?: LoanType) => {
    setEditMode('lender');
    setEditingItem(null);
    setEditingLoanType(loanType || 'home_loan');
    setEditingLenderId('');
    setEditingLenderName('');
    setEditingName('');
    setEditingDescription('');
    setEditingStatus('draft');
    setEditingCategories([]);
    setEditorOpen(true);
  };

  // Open editor for existing lender form
  const handleEditLenderForm = (form: ChecklistForm) => {
    setEditMode('lender');
    setEditingItem(form);
    setEditingLoanType(form.loanType);
    setEditingLenderId(form.lenderId || '');
    setEditingLenderName(form.lenderName || '');
    setEditingName(form.name);
    setEditingDescription(form.description || '');
    setEditingStatus(form.status);
    setEditingCategories(JSON.parse(JSON.stringify(form.categories))); // Deep clone
    setEditorOpen(true);
  };

  // Copy from base template
  const handleCopyFromTemplate = () => {
    const baseTemplate = templates.find(t => t.loanType === editingLoanType);
    if (baseTemplate) {
      setEditingCategories(JSON.parse(JSON.stringify(baseTemplate.categories)));
      toast.success('Copied from base template');
    } else {
      toast.error('No base template found for this loan type');
    }
  };

  // Save template or lender form
  const handleSave = async () => {
    // Validation
    if (!editingName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (editingCategories.length === 0) {
      toast.error('Please add at least one category');
      return;
    }
    if (editMode === 'lender' && !editingLenderId) {
      toast.error('Please select a lender');
      return;
    }

    setSaving(true);
    try {
      if (editMode === 'template') {
        // Save template
        const templateData: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
          loanType: editingLoanType,
          name: editingName,
          description: editingDescription,
          categories: editingCategories,
          version: editingItem ? (editingItem as ChecklistTemplate).version : 1,
          isActive: true,
        };

        if (editingItem) {
          await updateChecklistTemplate(editingLoanType, templateData);
          toast.success('Template updated successfully');
        } else {
          await createChecklistTemplate(templateData);
          toast.success('Template created successfully');
        }
      } else {
        // Save lender form
        const baseTemplate = templates.find(t => t.loanType === editingLoanType);
        if (!baseTemplate) {
          toast.error('Base template not found');
          return;
        }

        const formData: Omit<ChecklistForm, 'id' | 'createdAt' | 'updatedAt'> = {
          loanType: editingLoanType,
          lenderId: editingLenderId,
          lenderName: editingLenderName,
          baseTemplateId: baseTemplate.id,
          name: editingName,
          description: editingDescription,
          categories: editingCategories,
          version: editingItem ? (editingItem as ChecklistForm).version : 1,
          isActive: true,
          status: editingStatus,
        };

        if (editingItem) {
          await updateLenderChecklistForm((editingItem as ChecklistForm).id, formData);
          toast.success('Lender checklist updated successfully');
        } else {
          await createLenderChecklistForm(formData);
          toast.success('Lender checklist created successfully');
        }
      }

      setEditorOpen(false);
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Category operations
  const handleAddCategory = () => {
    setCategoryName('');
    setCategoryDescription('');
    setEditingCategoryIndex(null);
    setCategoryEditorOpen(true);
  };

  const handleEditCategory = (index: number) => {
    const category = editingCategories[index];
    setCategoryName(category.name);
    setCategoryDescription(category.description || '');
    setEditingCategoryIndex(index);
    setCategoryEditorOpen(true);
  };

  const handleSaveCategory = () => {
    if (!categoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    const newCategories = [...editingCategories];
    if (editingCategoryIndex !== null) {
      // Edit existing
      newCategories[editingCategoryIndex] = {
        ...newCategories[editingCategoryIndex],
        name: categoryName,
        description: categoryDescription,
      };
    } else {
      // Add new
      const newCategory: ChecklistCategory = {
        id: `category_${Date.now()}`,
        name: categoryName,
        description: categoryDescription,
        order: editingCategories.length + 1,
        items: [],
      };
      newCategories.push(newCategory);
    }
    setEditingCategories(newCategories);
    setCategoryEditorOpen(false);
    toast.success(editingCategoryIndex !== null ? 'Category updated' : 'Category added');
  };

  const handleDeleteCategory = (index: number) => {
    if (confirm('Delete this category and all its items?')) {
      const newCategories = editingCategories.filter((_, i) => i !== index);
      // Reorder
      newCategories.forEach((cat, i) => {
        cat.order = i + 1;
      });
      setEditingCategories(newCategories);
      toast.success('Category deleted');
    }
  };

  const handleMoveCategoryUp = (index: number) => {
    if (index === 0) return;
    const newCategories = [...editingCategories];
    [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
    newCategories.forEach((cat, i) => {
      cat.order = i + 1;
    });
    setEditingCategories(newCategories);
  };

  const handleMoveCategoryDown = (index: number) => {
    if (index === editingCategories.length - 1) return;
    const newCategories = [...editingCategories];
    [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
    newCategories.forEach((cat, i) => {
      cat.order = i + 1;
    });
    setEditingCategories(newCategories);
  };

  // Item operations
  const handleAddItem = (categoryIndex: number) => {
    setItemLabel('');
    setItemDescription('');
    setItemHelpText('');
    setItemRequired(false);
    setEditingItemCategoryIndex(categoryIndex);
    setEditingItemIndex(null);
    setItemEditorOpen(true);
  };

  const handleEditItem = (categoryIndex: number, itemIndex: number) => {
    const item = editingCategories[categoryIndex].items[itemIndex];
    setItemLabel(item.label);
    setItemDescription(item.description || '');
    setItemHelpText(item.helpText || '');
    setItemRequired(item.isRequired);
    setEditingItemCategoryIndex(categoryIndex);
    setEditingItemIndex(itemIndex);
    setItemEditorOpen(true);
  };

  const handleSaveItem = () => {
    if (!itemLabel.trim()) {
      toast.error('Please enter an item label');
      return;
    }
    if (editingItemCategoryIndex === null) return;

    const newCategories = [...editingCategories];
    const category = newCategories[editingItemCategoryIndex];

    if (editingItemIndex !== null) {
      // Edit existing
      category.items[editingItemIndex] = {
        ...category.items[editingItemIndex],
        label: itemLabel,
        description: itemDescription,
        helpText: itemHelpText,
        isRequired: itemRequired,
      };
    } else {
      // Add new
      const newItem: ChecklistItem = {
        id: `item_${Date.now()}`,
        label: itemLabel,
        description: itemDescription,
        order: category.items.length + 1,
        isRequired: itemRequired,
        helpText: itemHelpText,
      };
      category.items.push(newItem);
    }

    setEditingCategories(newCategories);
    setItemEditorOpen(false);
    toast.success(editingItemIndex !== null ? 'Item updated' : 'Item added');
  };

  const handleDeleteItem = (categoryIndex: number, itemIndex: number) => {
    if (confirm('Delete this item?')) {
      const newCategories = [...editingCategories];
      newCategories[categoryIndex].items = newCategories[categoryIndex].items.filter((_, i) => i !== itemIndex);
      // Reorder
      newCategories[categoryIndex].items.forEach((item, i) => {
        item.order = i + 1;
      });
      setEditingCategories(newCategories);
      toast.success('Item deleted');
    }
  };

  const handleMoveItemUp = (categoryIndex: number, itemIndex: number) => {
    if (itemIndex === 0) return;
    const newCategories = [...editingCategories];
    const items = newCategories[categoryIndex].items;
    [items[itemIndex - 1], items[itemIndex]] = [items[itemIndex], items[itemIndex - 1]];
    items.forEach((item, i) => {
      item.order = i + 1;
    });
    setEditingCategories(newCategories);
  };

  const handleMoveItemDown = (categoryIndex: number, itemIndex: number) => {
    const newCategories = [...editingCategories];
    const items = newCategories[categoryIndex].items;
    if (itemIndex === items.length - 1) return;
    [items[itemIndex], items[itemIndex + 1]] = [items[itemIndex + 1], items[itemIndex]];
    items.forEach((item, i) => {
      item.order = i + 1;
    });
    setEditingCategories(newCategories);
  };

  // Publish/Unpublish lender form
  const handleTogglePublish = async (form: ChecklistForm) => {
    const newStatus = form.status === 'published' ? 'draft' : 'published';
    try {
      await updateLenderChecklistForm(form.id, { status: newStatus });
      toast.success(newStatus === 'published' ? 'Published successfully' : 'Unpublished successfully');
      await loadData();
    } catch (error: any) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  // Render editor content (shared between Dialog and Sheet)
  const renderEditorContent = () => (
    <div className="space-y-6 py-4">
      {/* Basic Info */}
      <div className="space-y-4">
        <div className={cn("grid gap-4", isMobile ? "grid-cols-1" : "grid-cols-2")}>
          <div className="space-y-2">
            <Label>Loan Type</Label>
            <Select value={editingLoanType} onValueChange={(v) => setEditingLoanType(v as LoanType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOAN_TYPES.map(lt => (
                  <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {editMode === 'lender' && (
            <div className="space-y-2">
              <Label>Lender</Label>
              <Popover open={lenderComboboxOpen} onOpenChange={setLenderComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={lenderComboboxOpen}
                    className="w-full justify-between"
                  >
                    {editingLenderId
                      ? LENDERS.find((lender) => lender.id === editingLenderId)?.name
                      : "Select lender..."}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command filter={commandFilter}>
                    <CommandInput placeholder="Search lender..." />
                    <CommandList>
                      <CommandEmpty>No lender found.</CommandEmpty>
                      <CommandGroup>
                        {LENDERS.map((lender) => (
                          <CommandItem
                            key={lender.id}
                            value={lender.name}
                            onSelect={() => {
                              setEditingLenderId(lender.id);
                              setEditingLenderName(lender.name);
                              setLenderComboboxOpen(false);
                            }}
                          >
                            <Building2 className={cn(
                              "mr-2 h-4 w-4",
                              editingLenderId === lender.id ? "opacity-100" : "opacity-40"
                            )} />
                            {lender.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            placeholder="e.g., Home Loan Checklist"
          />
        </div>

        <div className="space-y-2">
          <Label>Description (Optional)</Label>
          <Textarea
            value={editingDescription}
            onChange={(e) => setEditingDescription(e.target.value)}
            placeholder="Brief description of this checklist"
            rows={2}
          />
        </div>

        {editMode === 'lender' && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={editingStatus} onValueChange={(v) => setEditingStatus(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only published checklists will be used for new leads
            </p>
          </div>
        )}

        {editMode === 'lender' && !editingItem && (
          <Button variant="outline" onClick={handleCopyFromTemplate} className="w-full">
            <Copy className="h-4 w-4 mr-2" />
            Copy from Base Template
          </Button>
        )}
      </div>

      <Separator />

      {/* Categories */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Categories</h3>
          <Button size="sm" onClick={handleAddCategory}>
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>

        {editingCategories.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No categories added yet. Click "Add Category" to get started.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {editingCategories.map((category, catIndex) => (
              <Card key={category.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">{category.name}</CardTitle>
                      {category.description && (
                        <CardDescription className="text-sm mt-1">
                          {category.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveCategoryUp(catIndex)}
                        disabled={catIndex === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveCategoryDown(catIndex)}
                        disabled={catIndex === editingCategories.length - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditCategory(catIndex)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(catIndex)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {category.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No items added</p>
                    ) : (
                      category.items.map((item, itemIndex) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-2 rounded border"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{item.label}</span>
                            {item.isRequired && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveItemUp(catIndex, itemIndex)}
                              disabled={itemIndex === 0}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveItemDown(catIndex, itemIndex)}
                              disabled={itemIndex === category.items.length - 1}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditItem(catIndex, itemIndex)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteItem(catIndex, itemIndex)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleAddItem(catIndex)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading checklists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Checklist Management</h1>
        <p className="text-muted-foreground mt-2">
          Manage checklist templates and lender-specific customizations
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
        <TabsList>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            Base Templates
          </TabsTrigger>
          <TabsTrigger value="lenders">
            <Building2 className="h-4 w-4 mr-2" />
            Lender Checklists
          </TabsTrigger>
        </TabsList>

        {/* Base Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Base templates are the foundation for all lender-specific checklists
            </p>
            <Select onValueChange={(loanType) => handleCreateTemplate(loanType as LoanType)}>
              <SelectTrigger className="w-[200px]">
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </SelectTrigger>
              <SelectContent>
                {LOAN_TYPES.map(lt => (
                  <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {LOAN_TYPES.find(lt => lt.value === template.loanType)?.label}
                      </CardDescription>
                    </div>
                    <Badge variant={template.isActive ? 'default' : 'secondary'}>
                      v{template.version}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      {template.categories.length} categories • {' '}
                      {template.categories.reduce((sum, cat) => sum + cat.items.length, 0)} items
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {templates.length === 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No templates found. Run the migration script: <code>window.migrateChecklistTemplates()</code>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* Lender Checklists Tab */}
        <TabsContent value="lenders" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Create customized checklists for specific lenders
            </p>
            <Button onClick={() => handleCreateLenderForm()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Lender Checklist
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {lenderForms.map((form) => (
              <Card key={form.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{form.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {form.lenderName} • {LOAN_TYPES.find(lt => lt.value === form.loanType)?.label}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant={form.status === 'published' ? 'default' : 'secondary'}>
                        {form.status}
                      </Badge>
                      <Badge variant="outline">v{form.version}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      {form.categories.length} categories • {' '}
                      {form.categories.reduce((sum, cat) => sum + cat.items.length, 0)} items
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEditLenderForm(form)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant={form.status === 'published' ? 'secondary' : 'default'}
                        size="sm"
                        onClick={() => handleTogglePublish(form)}
                      >
                        {form.status === 'published' ? (
                          <Lock className="h-4 w-4" />
                        ) : (
                          <Unlock className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {lenderForms.length === 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No lender checklists found. Create one by clicking the button above.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>

      {/* Main Editor - Dialog for Desktop, Sheet for Mobile */}
      {isMobile ? (
        <Sheet open={editorOpen} onOpenChange={setEditorOpen}>
          <SheetContent side="bottom" className="h-[90vh]">
            <SheetHeader>
              <SheetTitle>
                {editingItem ? 'Edit' : 'Create'} {editMode === 'template' ? 'Template' : 'Lender Checklist'}
              </SheetTitle>
              <SheetDescription>
                {editMode === 'template'
                  ? 'Base templates are used as the foundation for lender-specific checklists'
                  : 'Customize the checklist for a specific lender'}
              </SheetDescription>
            </SheetHeader>

            <ScrollArea className="h-[calc(90vh-140px)] mt-4">
              {renderEditorContent()}
            </ScrollArea>

            <SheetFooter className="mt-4">
              <Button variant="outline" onClick={() => setEditorOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Edit' : 'Create'} {editMode === 'template' ? 'Template' : 'Lender Checklist'}
              </DialogTitle>
              <DialogDescription>
                {editMode === 'template'
                  ? 'Base templates are used as the foundation for lender-specific checklists'
                  : 'Customize the checklist for a specific lender'}
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              {renderEditorContent()}
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Category Editor Dialog */}
      <Dialog open={categoryEditorOpen} onOpenChange={setCategoryEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategoryIndex !== null ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="e.g., Identity Documents"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Brief description"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Editor Dialog */}
      <Dialog open={itemEditorOpen} onOpenChange={setItemEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItemIndex !== null ? 'Edit Item' : 'Add Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Item Label</Label>
              <Input
                value={itemLabel}
                onChange={(e) => setItemLabel(e.target.value)}
                placeholder="e.g., PAN Card"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Help Text (Optional)</Label>
              <Textarea
                value={itemHelpText}
                onChange={(e) => setItemHelpText(e.target.value)}
                placeholder="Helpful information for the user"
                rows={2}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="required"
                checked={itemRequired}
                onCheckedChange={(checked) => setItemRequired(checked as boolean)}
              />
              <label
                htmlFor="required"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Required item
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemEditorOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveItem}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
